if (!customElements.get('responsive-anchor-tabs')) {
  class ResponsiveAnchorTabs extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.anchor-tab-responsive__list');
      this.items = Array.from(this.querySelectorAll('.anchor-tab-responsive__item'));
      this.sectionRoot = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
      this.observedSections = [];
      this.abortController = new AbortController();
      this.programmaticScrollController = null;
      this.programmaticScrollItem = null;
      this.bind();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.programmaticScrollController?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
    }

    getTargetId(item) {
      const id = item.dataset.anchorTarget;
      if (item.dataset.shopifyTarget !== 'true' || id.startsWith('shopify-section-')) return id;
      return `shopify-section-${this.dataset.sectionId.split('__')[0]}__${id}`;
    }

    getTarget(item) {
      const sectionTarget = document.getElementById(this.getTargetId(item));
      const childTargetId = item.dataset.anchorChildTarget;
      if (!childTargetId) return sectionTarget;

      const childTarget = document.getElementById(childTargetId);
      return sectionTarget?.contains(childTarget) ? childTarget : null;
    }

    setActive(item, center = true) {
      if (!item) return;
      this.items.forEach((entry) => {
        const active = entry === item;
        entry.classList.toggle('is-active', active);
        entry.querySelector('[role="tab"]')?.setAttribute('aria-selected', String(active));
      });
      if (center) this.centerItem(item);
    }

    centerItem(item) {
      if (!this.list) return;

      const listRect = this.list.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const centeredOffset = itemRect.left - listRect.left - (listRect.width - itemRect.width) / 2;
      const maxScroll = Math.max(0, this.list.scrollWidth - this.list.clientWidth);
      const left = Math.max(0, Math.min(this.list.scrollLeft + centeredOffset, maxScroll));
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

      this.list.scrollTo({ left, behavior });
    }

    scrollToTarget(item) {
      const target = this.getTarget(item);
      if (!target) return;
      const offset = this.sectionRoot?.offsetHeight || 0;
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

      this.lockProgrammaticScroll(item, behavior);
      window.scrollTo({ top: Math.max(0, top), behavior });
    }

    lockProgrammaticScroll(item, behavior) {
      this.programmaticScrollController?.abort();

      const controller = new AbortController();
      const signal = controller.signal;
      let settleTimer;

      this.programmaticScrollController = controller;
      this.programmaticScrollItem = item;

      const finish = () => {
        if (signal.aborted || this.programmaticScrollController !== controller) return;

        window.clearTimeout(settleTimer);
        this.programmaticScrollController = null;
        this.programmaticScrollItem = null;
        controller.abort();
        this.setActive(item, false);
      };

      const scheduleFinish = () => {
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(finish, 180);
      };

      signal.addEventListener('abort', () => window.clearTimeout(settleTimer), { once: true });

      if (behavior === 'auto') {
        requestAnimationFrame(finish);
        return;
      }

      window.addEventListener('scroll', scheduleFinish, { passive: true, signal });
      if ('onscrollend' in window) {
        window.addEventListener('scrollend', finish, { once: true, signal });
      }
      settleTimer = window.setTimeout(finish, 1200);
    }

    updateOverflow = () => {
      this.list?.classList.toggle('is-overflowing', this.list.scrollWidth > this.list.clientWidth + 1);
    };

    handleWheel = (event) => {
      if (!window.matchMedia('(min-width: 1280px)').matches || this.list.scrollWidth <= this.list.clientWidth) return;
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (!delta) return;
      event.preventDefault();
      this.list.scrollLeft += delta;
    };

    bind() {
      if (!this.list) return;
      const signal = this.abortController.signal;

      this.items.forEach((item) => {
        item.querySelector('[role="tab"]')?.addEventListener('click', () => {
          this.setActive(item);
          this.scrollToTarget(item);
        }, { signal });

        const target = this.getTarget(item);
        if (target) this.observedSections.push({ item, target });
      });

      this.list.addEventListener('wheel', this.handleWheel, { passive: false, signal });
      window.addEventListener('resize', this.updateOverflow, { passive: true, signal });
      this.resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(this.updateOverflow) : null;
      this.resizeObserver?.observe(this.list);
      this.updateOverflow();

      if ('IntersectionObserver' in window && this.observedSections.length) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          if (this.programmaticScrollItem) return;

          const visible = entries.find((entry) => entry.isIntersecting);
          const match = this.observedSections.find(({ target }) => target === visible?.target);
          this.setActive(match?.item, false);
        }, {
          rootMargin: `-${this.sectionRoot?.offsetHeight || 0}px 0px -60% 0px`,
          threshold: [0, 0.2, 0.5, 1]
        });
        this.observedSections.forEach(({ target }) => this.intersectionObserver.observe(target));
      }
    }
  }

  customElements.define('responsive-anchor-tabs', ResponsiveAnchorTabs);
}
